/**
 * 用户Vuex模块
 * 管理用户信息和状态
 */

const state = {
  // 用户信息
  userInfo: {
    id: null,
    name: '访客用户',
    avatar: null,
    email: null,
    phone: null,
    learningTime: 0,
    completedCourses: 0,
    creativesCount: 0,
    collectionsCount: 0
  },
  // 登录状态
  isLoggedIn: false,
  // 用户偏好设置
  preferences: {
    theme: 'light',
    language: 'zh-CN',
    notifications: {
      courses: true,
      activities: true,
      system: true
    }
  },
  // 学习记录
  learningHistory: [],
  // 收藏列表
  favorites: [],
  // 创作作品
  creativeWorks: []
}

const mutations = {
  /**
   * 设置用户信息
   * @param {Object} state - 状态对象
   * @param {Object} userInfo - 用户信息
   */
  SET_USER_INFO(state, userInfo) {
    state.userInfo = { ...state.userInfo, ...userInfo }
  },

  /**
   * 设置登录状态
   * @param {Object} state - 状态对象
   * @param {boolean} status - 登录状态
   */
  SET_LOGIN_STATUS(state, status) {
    state.isLoggedIn = status
  },

  /**
   * 设置用户偏好
   * @param {Object} state - 状态对象
   * @param {Object} preferences - 偏好设置
   */
  SET_PREFERENCES(state, preferences) {
    state.preferences = { ...state.preferences, ...preferences }
  },

  /**
   * 添加学习记录
   * @param {Object} state - 状态对象
   * @param {Object} record - 学习记录
   */
  ADD_LEARNING_RECORD(state, record) {
    state.learningHistory.unshift(record)
  },

  /**
   * 添加收藏
   * @param {Object} state - 状态对象
   * @param {Object} item - 收藏项
   */
  ADD_FAVORITE(state, item) {
    const exists = state.favorites.some(fav => fav.id === item.id)
    if (!exists) {
      state.favorites.unshift({
        ...item,
        addedAt: new Date().toISOString()
      })
    }
  },

  /**
   * 移除收藏
   * @param {Object} state - 状态对象
   * @param {string} itemId - 项目ID
   */
  REMOVE_FAVORITE(state, itemId) {
    state.favorites = state.favorites.filter(fav => fav.id !== itemId)
  },

  /**
   * 添加创作作品
   * @param {Object} state - 状态对象
   * @param {Object} work - 作品数据
   */
  ADD_CREATIVE_WORK(state, work) {
    state.creativeWorks.unshift({
      ...work,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    })
  },

  /**
   * 更新统计数据
   * @param {Object} state - 状态对象
   * @param {Object} stats - 统计数据
   */
  UPDATE_STATS(state, stats) {
    state.userInfo = { ...state.userInfo, ...stats }
  }
}

const actions = {
  /**
   * 用户登录
   * @param {Object} context - Vuex context
   * @param {Object} credentials - 登录凭据
   */
  async login({ commit }, credentials) {
    try {
      // 这里应该调用实际的登录API
      // const response = await api.login(credentials)
      
      // 模拟登录成功
      const userInfo = {
        id: '1',
        name: credentials.username || '张三',
        email: credentials.email || 'zhangsan@example.com',
        avatar: null,
        learningTime: 120,
        completedCourses: 5,
        creativesCount: 3,
        collectionsCount: 12
      }

      commit('SET_USER_INFO', userInfo)
      commit('SET_LOGIN_STATUS', true)

      // 保存到本地存储
      localStorage.setItem('userInfo', JSON.stringify(userInfo))
      localStorage.setItem('isLoggedIn', 'true')

      return { success: true, userInfo }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  /**
   * 用户注册
   * @param {Object} context - Vuex context
   * @param {Object} userData - 注册数据
   */
  async register({ commit }, userData) {
    try {
      // 这里应该调用实际的注册API
      // const response = await api.register(userData)
      
      // 模拟注册成功
      const userInfo = {
        id: Date.now().toString(),
        name: userData.username,
        email: userData.email,
        avatar: null,
        learningTime: 0,
        completedCourses: 0,
        creativesCount: 0,
        collectionsCount: 0
      }

      commit('SET_USER_INFO', userInfo)
      commit('SET_LOGIN_STATUS', true)

      return { success: true, userInfo }
    } catch (error) {
      return { success: false, error: error.message }
    }
  },

  /**
   * 用户登出
   * @param {Object} context - Vuex context
   */
  logout({ commit }) {
    commit('SET_USER_INFO', {
      id: null,
      name: '访客用户',
      avatar: null,
      email: null,
      phone: null,
      learningTime: 0,
      completedCourses: 0,
      creativesCount: 0,
      collectionsCount: 0
    })
    commit('SET_LOGIN_STATUS', false)

    // 清除本地存储
    localStorage.removeItem('userInfo')
    localStorage.removeItem('isLoggedIn')
  },

  /**
   * 初始化用户状态
   * @param {Object} context - Vuex context
   */
  initUserState({ commit }) {
    const savedUserInfo = localStorage.getItem('userInfo')
    const savedLoginStatus = localStorage.getItem('isLoggedIn')

    if (savedUserInfo && savedLoginStatus === 'true') {
      commit('SET_USER_INFO', JSON.parse(savedUserInfo))
      commit('SET_LOGIN_STATUS', true)
    }
  },

  /**
   * 更新用户信息
   * @param {Object} context - Vuex context
   * @param {Object} userInfo - 用户信息
   */
  updateUserInfo({ commit, state }, userInfo) {
    commit('SET_USER_INFO', userInfo)
    
    if (state.isLoggedIn) {
      localStorage.setItem('userInfo', JSON.stringify(state.userInfo))
    }
  },

  /**
   * 添加到收藏
   * @param {Object} context - Vuex context
   * @param {Object} item - 收藏项
   */
  addToFavorites({ commit }, item) {
    commit('ADD_FAVORITE', item)
  },

  /**
   * 从收藏中移除
   * @param {Object} context - Vuex context
   * @param {string} itemId - 项目ID
   */
  removeFromFavorites({ commit }, itemId) {
    commit('REMOVE_FAVORITE', itemId)
  },

  /**
   * 记录学习行为
   * @param {Object} context - Vuex context
   * @param {Object} record - 学习记录
   */
  recordLearning({ commit }, record) {
    commit('ADD_LEARNING_RECORD', record)
  }
}

const getters = {
  /**
   * 获取用户信息
   * @param {Object} state - 状态对象
   * @returns {Object} 用户信息
   */
  getUserInfo: state => state.userInfo,

  /**
   * 获取登录状态
   * @param {Object} state - 状态对象
   * @returns {boolean} 登录状态
   */
  isLoggedIn: state => state.isLoggedIn,

  /**
   * 获取用户偏好
   * @param {Object} state - 状态对象
   * @returns {Object} 偏好设置
   */
  getPreferences: state => state.preferences,

  /**
   * 获取学习历史
   * @param {Object} state - 状态对象
   * @returns {Array} 学习历史
   */
  getLearningHistory: state => state.learningHistory,

  /**
   * 获取收藏列表
   * @param {Object} state - 状态对象
   * @returns {Array} 收藏列表
   */
  getFavorites: state => state.favorites,

  /**
   * 获取创作作品
   * @param {Object} state - 状态对象
   * @returns {Array} 创作作品
   */
  getCreativeWorks: state => state.creativeWorks,

  /**
   * 检查是否已收藏
   * @param {Object} state - 状态对象
   * @returns {Function} 检查函数
   */
  isFavorited: state => itemId => {
    return state.favorites.some(fav => fav.id === itemId)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 